/**
 * useCreateFlowStep Hook
 * Pure step transitions + guards for the rival-first create flow
 * (specs/challenge-flow-redesign — Stories 1/2/4/6).
 *
 * Order: rival → build → review → success.
 * The pure `resolveStep` reducer is exported for unit testing.
 */

import { useCallback } from 'react';
import type { CreateStep } from '../types/create.types';

// One pick is a valid challenge (user decision 2026-07-07); server never enforced a minimum.
export const MIN_PICKS_TO_REVIEW = 1;

export type CreateFlowAction = 'toBuild' | 'toReview' | 'back';

export interface CreateFlowGuardCtx {
  hasRival: boolean;
  pickCount: number;
  minPicks?: number;
}

/**
 * Pure transition. Returns the next step, or the SAME step when a guard blocks it.
 * Callers compare `resolveStep(...) !== current` to know if the move was allowed.
 */
export const resolveStep = (
  current: CreateStep,
  action: CreateFlowAction,
  ctx: CreateFlowGuardCtx
): CreateStep => {
  const minPicks = ctx.minPicks ?? MIN_PICKS_TO_REVIEW;
  switch (action) {
    case 'toBuild':
      return ctx.hasRival ? 'build' : current;
    case 'toReview':
      return ctx.hasRival && ctx.pickCount >= minPicks ? 'review' : current;
    case 'back':
      if (current === 'build') return 'rival';
      if (current === 'review') return 'build';
      return current;
    default:
      return current;
  }
};

export const canReview = (ctx: CreateFlowGuardCtx): boolean =>
  ctx.hasRival && ctx.pickCount >= (ctx.minPicks ?? MIN_PICKS_TO_REVIEW);

export interface UseCreateFlowStepArgs {
  step: CreateStep;
  setStep: (next: CreateStep) => void;
  hasRival: boolean;
  pickCount: number;
  minPicks?: number;
}

export interface UseCreateFlowStepReturn {
  toBuild: () => void;
  toReview: () => void;
  back: () => void;
  canReview: boolean;
}

export const useCreateFlowStep = ({
  step,
  setStep,
  hasRival,
  pickCount,
  minPicks = MIN_PICKS_TO_REVIEW,
}: UseCreateFlowStepArgs): UseCreateFlowStepReturn => {
  const ctx: CreateFlowGuardCtx = { hasRival, pickCount, minPicks };

  const toBuild = useCallback(() => {
    const next = resolveStep(step, 'toBuild', ctx);
    if (next !== step) setStep(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, setStep, hasRival, pickCount, minPicks]);

  const toReview = useCallback(() => {
    const next = resolveStep(step, 'toReview', ctx);
    if (next !== step) setStep(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, setStep, hasRival, pickCount, minPicks]);

  const back = useCallback(() => {
    const next = resolveStep(step, 'back', ctx);
    if (next !== step) setStep(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, setStep]);

  return { toBuild, toReview, back, canReview: canReview(ctx) };
};
